import React from 'react';
import { services, salonInfo } from '../data/mock';
import { useLanguage } from '../i18n/LanguageContext';
import { CinderellaProgram } from './CinderellaProgram';

export const Services = () => {
  const { t } = useLanguage();
  const openWhatsApp = () => {
    window.open(`https://wa.me/${salonInfo.whatsapp.replace(/\s|\+/g, '')}`, '_blank');
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#0e0e11] text-neutral-100"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-4 mb-8">
            <span className="divider-line"></span>
            <span className="eyebrow text-lilac">{t.services.eyebrow}</span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-neutral-100 tracking-tight mb-8">
            {t.services.title}
          </h2>
          <div className="space-y-5 max-w-2xl">
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed font-light">
              {t.services.p1}
            </p>
            <p className="text-base md:text-lg text-neutral-500 leading-relaxed font-light">
              {t.services.p2}
            </p>
          </div>
        </div>

        {/* Premium message / brand positioning */}
        <div
          data-testid="premium-message"
          className="max-w-4xl mb-16 md:mb-20 border-l-2 border-lilac/40 pl-7 md:pl-10"
        >
          <p className="eyebrow text-lilac mb-4">{t.services.premiumEyebrow}</p>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-neutral-100 tracking-tight mb-6">
            {t.services.premiumTitleA}{' '}
            <span className="italic text-lilac font-light">{t.services.premiumTitleB}</span>
          </h3>
          <p className="font-display italic text-xl md:text-2xl text-neutral-300 font-light mb-6">
            {t.services.premiumIntro}
          </p>
          <div className="space-y-4 max-w-2xl">
            <p className="text-base md:text-lg text-neutral-400 leading-relaxed font-light">
              {t.services.premiumP1}
            </p>
            <p className="text-base md:text-lg text-neutral-400 leading-relaxed font-light">
              {t.services.premiumP2}
            </p>
          </div>
          <p className="text-sm md:text-base text-sage font-light mt-6 leading-relaxed">
            {t.services.includedInfo}
          </p>
        </div>

        {/* 1. 20 € Neukundenrabatt — HERO promo box */}
        <div
          data-testid="services-promo-box"
          className="relative mb-20 border border-lilac/30 bg-gradient-to-br from-lilac/[0.08] via-white/[0.02] to-transparent px-8 md:px-14 py-10 md:py-14 overflow-hidden"
        >
          <p className="eyebrow text-lilac mb-6">{t.services.promoBoxTitle}</p>
          <div className="flex items-baseline gap-5 md:gap-7 mb-4 flex-wrap">
            <span className="font-display text-[6rem] md:text-[9rem] lg:text-[11rem] text-lilac tracking-tight leading-[0.9]">
              {t.services.promoBoxAmount}
            </span>
            <span className="font-display text-3xl md:text-5xl lg:text-6xl text-neutral-100 leading-tight tracking-tight">
              {t.services.promoBoxAction}
            </span>
          </div>
          <p className="font-display italic text-xl md:text-2xl lg:text-3xl text-neutral-300 font-light mt-4 mb-8">
            {t.services.promoBoxFor}
          </p>
          <p className="text-sm md:text-base text-neutral-500 font-light max-w-xl">
            {t.services.promoBoxNote}
          </p>
        </div>
      </div>

      {/* 2. Cinderella Skin Program (+ 3. Folgebuchung inside) */}
      <CinderellaProgram />

      {/* 4. Individual treatments */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 mt-24 md:mt-32">
        <div className="mb-12">
          <p className="eyebrow text-lilac mb-4">{t.services.individualLabel || 'Einzelbehandlungen'}</p>
          <h3 className="font-display text-4xl md:text-5xl text-neutral-100 tracking-tight">
            {t.services.individualTitle || 'Alle Behandlungen.'}
          </h3>
        </div>

        <div className="border-t border-white/10">
          {services.map((service) => (
            <div
              key={service.id}
              data-testid={`service-${service.id}`}
              className={`group grid grid-cols-12 gap-6 py-8 border-b border-white/10 transition-colors duration-500 px-2 ${
                service.featured
                  ? 'bg-lilac/[0.05] border-l-2 border-l-lilac/60 hover:bg-lilac/[0.07]'
                  : 'hover:bg-white/[0.02]'
              }`}
            >
              <div className="col-span-12 md:col-span-7">
                {service.featured && (
                  <p className="eyebrow text-lilac mb-2">
                    {service.gift ? t.services.giftBadge : 'Signature Treatment'}
                  </p>
                )}
                <h3 className="font-display text-2xl md:text-3xl text-neutral-100 mb-3 leading-tight">
                  {service.name}
                </h3>
                <div className="text-neutral-400 font-light leading-relaxed max-w-xl space-y-3">
                  {service.description.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                {service.gift && (
                  <p className="eyebrow text-sage mt-4">{t.services.giftNote}</p>
                )}
                {service.excludedFromDiscounts && (
                  <p
                    data-testid={`service-${service.id}-discount-exclusion`}
                    className="eyebrow text-lilac mt-3"
                  >
                    {t.services.discountsExcluded}
                  </p>
                )}
                {service.packages && (
                  <div className="mt-5 border-t border-white/10 pt-4">
                    <p className="eyebrow text-lilac mb-3">{t.services.packagesLabel}</p>
                    <ul className="space-y-1.5">
                      {service.packages.map((pkg, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-4 text-neutral-300 font-light">
                          <span>{pkg.label}</span>
                          <span className="font-display text-lg text-lilac tracking-tight">{pkg.price}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="col-span-6 md:col-span-2 flex md:items-start md:pt-2">
                <span className="eyebrow text-neutral-500">{service.duration}</span>
              </div>

              <div className="col-span-6 md:col-span-3 flex md:items-start md:justify-end md:pt-1">
                <span className={`font-display text-3xl md:text-4xl tracking-tight ${
                  service.featured ? 'text-lilac' : 'text-neutral-100'
                }`}>
                  {service.price}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <p className="font-display italic text-2xl md:text-3xl text-neutral-300 max-w-xl font-light leading-snug">
            {t.services.ctaQuestion} <span className="text-lilac">{t.services.ctaQuestionAccent}</span>
          </p>
          <button
            onClick={openWhatsApp}
            data-testid="services-cta-whatsapp"
            className="group inline-flex items-center gap-4 bg-lilac hover:bg-white text-neutral-950 px-9 py-5 eyebrow transition-all duration-500"
          >
            {t.cta.whatsapp}
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </button>
        </div>
      </div>
    </section>
  );
};
